import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import {
  Mail,
  Calendar,
  Clock,
  FileText,
  Download,
  Sparkles,
  Tag,
  X,
  ChevronRight
} from 'lucide-react';

const UserDetailDrawer = ({ user, isOpen, onClose }) => {
  if (!user) return null;

  const getValueScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-blue-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getJourneyStageColor = (stage) => {
    switch (stage) {
      case 'paid': return 'text-emerald-500';
      case 'paywall': return 'text-orange-500';
      case 'completed': return 'text-blue-500';
      case 'started': return 'text-gray-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] p-0">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex-none border-b border-border/40">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background/0" />
              <div className="relative flex items-center justify-between gap-6 px-6 py-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-medium tracking-tight">User Details</h2>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* User Info */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{user.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>{user.email}</span>
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-sm",
                    getValueScoreColor(user.valueScore)
                  )}
                >
                  Score: {user.valueScore}
                </Badge>
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Joined {user.createdAt}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>Last active {user.lastActivity}</span>
                </div>
              </div>
            </div>

            {/* Journey Stage */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Journey Stage</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={cn(
                    "text-sm font-medium",
                    getJourneyStageColor(user.journeyStage)
                  )}>
                    {user.journeyStage.charAt(0).toUpperCase() + user.journeyStage.slice(1)}
                  </span>
                  {user.dropOffPoint && (
                    <span className="text-sm text-red-500">
                      Dropped off at {user.dropOffPoint}
                    </span>
                  )}
                </div>
                <Progress value={75} className="h-2" />
              </div>
            </div>

            {/* AI Features */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">AI Features Used</h4>
              <div className="flex flex-wrap gap-2">
                {user.aiFeaturesUsed.map((feature, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="text-xs bg-primary/5 hover:bg-primary/10 text-primary/70"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Resume Stats */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Resume Stats</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{user.resumesCreated}</div>
                    <div className="text-sm text-muted-foreground">Created</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{user.resumesDownloaded}</div>
                    <div className="text-sm text-muted-foreground">Downloaded</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {user.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className="text-xs border-border/20"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4">
              <Button className="w-full justify-between">
                View Resume History
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserDetailDrawer; 